import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2, HardHat, Search } from 'lucide-react';
import { InstallerForm } from './InstallerForm';
import { DeleteInstallerDialog } from './DeleteInstallerDialog';
import { Manager } from '@/types';
import { SlidePanel } from '@/components/ui/slide-panel';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export interface Installer {
  id?: string;
  name: string; // Maps to company_name
  address: string; // Maps to street
  city: string;
  state: string;
  zip_code: string;
  county: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  user_id?: string | null;
  internal_pm_manager_id?: string | null;
  internal_pm_manager_name?: string | null;
  installer_pm_manager_id?: string | null;
  installer_pm_manager_name?: string | null;
  biz_dev_rep_id?: string | null;
  biz_dev_rep_name?: string | null;
  email: string | null;
  phone: string | null;
  cost_ppw: number | null;
}

interface InstallersTabProps {
  installers: Installer[];
  managers: Manager[];
  onAddInstaller: (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => Promise<string>;
  onUpdateInstaller: (installerData: Installer) => Promise<string>;
  onDeleteInstaller: (installerId: string) => Promise<void>;
  isLoading: boolean;
}

export function InstallersTab({
  installers = [],
  managers = [],
  onAddInstaller,
  onUpdateInstaller,
  onDeleteInstaller,
  isLoading,
}: InstallersTabProps) {
  const { toast } = useToast();
  const [isAddingInstaller, setIsAddingInstaller] = useState(false);
  const [editingInstaller, setEditingInstaller] = useState<Installer | null>(null);
  const [deletingInstaller, setDeletingInstaller] = useState<Installer | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Removed debug logging to prevent console spam

  // Log any installers with invalid cost_ppw
  installers.forEach((installer, index) => {
    if (installer.cost_ppw === undefined) {
      console.warn(`Installer at index ${index} has undefined cost_ppw:`, installer);
    }
  });

  const handleAddInstaller = async (installerData: Omit<Installer, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const installerId = await onAddInstaller(installerData);
      setIsAddingInstaller(false);
      return installerId;
    } catch (error: any) {
      console.error('Error in handleAddInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleUpdateInstaller = async (installerData: Installer) => {
    try {
      if (!installerData.id) {
        throw new Error('Installer ID is required for updates');
      }
      const installerId = await onUpdateInstaller(installerData);
      setEditingInstaller(null);
      return installerId;
    } catch (error: any) {
      console.error('Error in handleUpdateInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteInstaller = async () => {
    if (!deletingInstaller?.id) return;
    try {
      await onDeleteInstaller(deletingInstaller.id);
      setDeletingInstaller(null);
    } catch (error: any) {
      console.error('Error in handleDeleteInstaller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete installer.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Filter installers based on search query
  const filteredInstallers = installers.filter(installer => 
    installer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (installer.internal_pm_manager_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (installer.installer_pm_manager_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (installer.biz_dev_rep_name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Search and Filter Controls */}
      <div className="flex justify-end items-center">
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search installers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48"
          />
          {/* Hidden button that can be triggered from parent */}
          <Button
            onClick={() => setIsAddingInstaller(true)}
            disabled={isLoading}
            className="hidden"
            data-add-installer-button
          >
            <HardHat className="mr-2 h-4 w-4" />
            Add Installer
          </Button>
        </div>
      </div>

      {/* Installers Table */}
      <div className="overflow-hidden border rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead className="font-semibold text-muted-foreground">Company Name</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Location</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Contact</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Internal PM</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Installer PM</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Biz Dev Rep</TableHead>
                <TableHead className="font-semibold text-muted-foreground">Cost/Watt</TableHead>
                <TableHead className="text-right font-semibold text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-card">
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading installers...
                  </TableCell>
                </TableRow>
              ) : filteredInstallers.length > 0 ? (
                filteredInstallers.map((installer) => (
                  <TableRow key={installer.id} className="hover:bg-muted">
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span className="text-sm text-foreground">{installer.name}</span>
                        <span className={`text-xs ${installer.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {installer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-foreground">
                        <div>{installer.address}</div>
                        <div className="text-xs text-muted-foreground">
                          {installer.city}, {installer.state} {installer.zip_code}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-foreground">
                        {installer.email && <div>{installer.email}</div>}
                        {installer.phone && <div className="text-xs text-muted-foreground">{installer.phone}</div>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{installer.internal_pm_manager_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{installer.installer_pm_manager_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">{installer.biz_dev_rep_name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-foreground">
                        {installer.cost_ppw ? `$${installer.cost_ppw.toFixed(2)}` : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-primary hover:bg-muted"
                        onClick={() => setEditingInstaller(installer)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="ml-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeletingInstaller(installer)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'No installers match your search criteria.' : 'No installers found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <SlidePanel
        isOpen={isAddingInstaller}
        onClose={() => setIsAddingInstaller(false)}
        title="Add Installer"
        width="2xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="text-sm text-muted-foreground mb-6">
          Enter the details for the new installer.
        </div>
        <InstallerForm
          initialData={null}
          onSubmit={handleAddInstaller}
          onCancel={() => setIsAddingInstaller(false)}
          isSubmitting={isLoading}
          managers={managers}
          isEditing={false}
        />
      </SlidePanel>

      <SlidePanel
        isOpen={!!editingInstaller}
        onClose={() => setEditingInstaller(null)}
        title="Edit Installer"
        width="2xl"
        topOffset="4.5rem"
        showEdgeCloseButton={true}
      >
        <div className="text-sm text-muted-foreground mb-6">
          Update the installer details.
        </div>
        <InstallerForm
          initialData={editingInstaller}
          onSubmit={handleUpdateInstaller}
          onCancel={() => setEditingInstaller(null)}
          isSubmitting={isLoading}
          managers={managers}
          isEditing={true}
        />
      </SlidePanel>

      <DeleteInstallerDialog
        open={!!deletingInstaller}
        onOpenChange={(open) => !open && setDeletingInstaller(null)}
        onConfirm={handleDeleteInstaller}
        installerName={deletingInstaller?.name || ''}
      />
    </div>
  );
}